import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, GlobalService } from '@core/index';
import {
  DateUtils,
  IButton,
  ToasterService,
  TranslateService,
} from '@devinforius/i-essentials';
import { ITabs } from '@devinforius/i-navigation';
import { IColumn } from '@devinforius/i-table';
import { Valve } from '@features/valve/models';
import { ValveService } from '@features/valve/services';
import { Role } from '@shared/enums/roles.enum';
import { StatusEnum } from '@shared/enums/status.enum';
import { ConfirmationService, LazyLoadEvent, MenuItem } from 'primeng/api';

@Component({
  selector: 'app-valve-list',
  templateUrl: './valve-list.component.html',
  styleUrl: './valve-list.component.scss',
})
export class ValveListComponent implements OnInit {
  private router: Router = inject(Router);
  private translateService: TranslateService = inject(TranslateService);
  private authService: AuthService = inject(AuthService);
  private confirmationService: ConfirmationService =
    inject(ConfirmationService);
  private toasterService: ToasterService = inject(ToasterService);
  private globalService: GlobalService = inject(GlobalService);
  public valveService: ValveService = inject(ValveService);

  public titleButtons!: IButton[];
  public columns!: IColumn[];
  public menu!: MenuItem[];
  public steps!: (ITabs & { title: string })[];
  public currentStep!: string;
  public loading: boolean = false;
  public tableLoading: boolean = false;
  public preventLoading: boolean = true;
  public items!: Valve[];

  public ngOnInit(): void {
    const firstStep = this.globalService.status?.[0]?.id?.toString() || '1';
    this.currentStep = firstStep;
    this.valveService.resetPagination();

    this.setTitleButtons();
    this.setSteps();

    if (sessionStorage.getItem('valve-list-step')) {
      this.changeStep(sessionStorage.getItem('valve-list-step') || this.steps[0].title);
    } else {
      this.setMenu(firstStep);
      this.setColumns(firstStep);
    }
  }

  public changeStep(step: string): void {
    this.loading = true;
    this.currentStep = step;
    sessionStorage.setItem('valve-list-step', step);

    this.valveService.resetPagination();

    this.setMenu(step);
    this.setColumns(step);
  }

  public openModalPublish(valveId: string): void {
    this.confirmationService.confirm({
      header: this.translateService.translate('modal.publication.title'),
      message: 'publication',
      key: 'global',
      acceptLabel: this.translateService.translate('modal.publication.accept'),
      acceptVisible: false,
      rejectVisible: false,
      acceptIcon: 'bi bi-cloud-arrow-up-fill',
      accept: () => {
        this.publishValve(valveId);
      },
    });
  }

  public onTableUpdate(event: LazyLoadEvent): void {
    if (!this.preventLoading) {
      this.tableLoading = true;

      this.valveService.currentValvePagination = {
        ...this.valveService.currentValvePagination,
        pageSize: event.rows || 50,
        pageNumber:
          event.first === 0 ? 1 : (event.first || 1) / (event.rows || 50) + 1,
      };

      this.getValves(this.currentStep);
    } else {
      this.preventLoading = false;
    }
  }

  private openModalCorrection(valveId: string): void {
    this.confirmationService.confirm({
      header: this.translateService.translate('modal.correction.title'),
      message: 'correction',
      key: 'global',
      acceptLabel: this.translateService.translate('modal.correction.accept'),
      acceptVisible: false,
      rejectVisible: false,
      acceptIcon: 'bi bi-pen-fill',
      accept: () => {
        this.sendToCorrection(valveId);
      },
    });
  }

  private openModalArchive(valveId: string): void {
    this.confirmationService.confirm({
      header: this.translateService.translate('modal.archive.title'),
      message: 'archive',
      key: 'global',
      acceptLabel: this.translateService.translate('modal.archive.accept'),
      acceptVisible: false,
      rejectVisible: false,
      acceptIcon: 'bi bi-archive-fill',
      accept: () => {
        this.archiveValve(valveId);
      },
    });
  }

  private setTitleButtons(): void {
    this.titleButtons = [
      {
        text: this.translateService.translate('valves.buttons.historical'),
        icon: 'icon-historic',
        iconPosition: 'right',
        styleClass: 'blue',
        click: () => this.router.navigate(['valves', 'historic']),
      },
      {
        text: this.translateService.translate('valves.buttons.add'),
        icon: 'bi bi-plus-circle-fill',
        iconPosition: 'right',
        styleClass: 'green',
        click: () => this.router.navigate(['valves', 'create']),
      },
    ];
  }

  private setSteps(): void {
    const valveStep = sessionStorage.getItem('valve-list-step');

    this.steps = this.globalService.status.map((item, index) => ({
      header: this.translateService.translate(
        'valves.stepper.' + item.name.toLowerCase()
      ),
      title: item.id.toString(),
      active: valveStep ? valveStep === item.id.toString() : index === 0,
      icon: '',
      slug: '',
    }));
  }

  private setColumns(step: string): void {
    this.columns = [];

    this.columns = [
      {
        field: 'title',
        header: this.translateService.translate('valves.list.title'),
      },
      {
        field: 'typeId',
        header: this.translateService.translate('valves.list.type'),
        format: (value: number) => {
          return (
            this.globalService.types.find((item) => item.id === value)?.type ??
            '-'
          );
        },
      },
      {
        field: 'natureId',
        header: this.translateService.translate('valves.list.nature'),
        format: (value: number) => {
          return (
            this.globalService.natures.find((item) => item.id === value)
              ?.description ?? '-'
          );
        },
      },
    ];

    switch (step) {
      case StatusEnum.DEPOSIT:
        this.columns.push({
          field: 'dateStart',
          header: this.translateService.translate('valves.list.creation-date'),
          format: (value: string) => {
            return DateUtils.format(value, 'DD/MM/YYYY');
          },
        });
        break;
      case StatusEnum.PUBLISHED:
        this.columns.push({
          field: 'datePublication',
          header: this.translateService.translate(
            'valves.list.publication-date'
          ),
          format: (value: string) => {
            return DateUtils.format(value, 'DD/MM/YYYY à HH:mm');
          },
        });
        break;
      case StatusEnum.TO_CORRECT:
        this.columns.push({
          field: 'dateCorrection',
          header: this.translateService.translate('valves.list.correction-date'),
          format: (value: string) => {
            return DateUtils.format(value);
          },
        });
        break;
      case StatusEnum.ARCHIVED:
        this.columns.push({
          field: 'dateArchive',
          header: this.translateService.translate('valves.list.archived-date'),
          format: (value: string) => {
            return DateUtils.format(value);
          },
        });
        break;
    }

    this.getValves(step);
  }

  private setMenu(step: string): void {
    this.menu = [];

    if (step === StatusEnum.DEPOSIT || step === StatusEnum.TO_CORRECT) {
      this.menu = [
        {
          label: this.translateService.translate('valves.list.menu.show'),
          icon: 'bi bi-pen-fill',
          command: (item: any) =>
            this.router.navigate(['valves', 'update', item.id]),
        },
      ];

      if (this.authService.haveRole(Role.publisher)) {
        this.menu.push({
          label: this.translateService.translate('valves.list.menu.publish'),
          icon: 'icon-stamp-black',
          command: (item: any) => this.openModalPublish(item.id),
        });
      }
    } else if (step === StatusEnum.PUBLISHED) {
      this.menu = [
        {
          label: this.translateService.translate('valves.list.menu.show'),
          icon: 'bi bi-eye-fill',
          command: (item: any) =>
            this.router.navigate(['valves', 'update', item.id]),
        },
      ];

      if (this.authService.haveRole(Role.publisher)) {
        this.menu.push(
          {
            label: this.translateService.translate('valves.list.menu.correct'),
            icon: 'bi bi-pen-fill',
            command: (item: any) => this.openModalCorrection(item.id),
          },
          {
            label: this.translateService.translate('valves.list.menu.archive'),
            icon: 'bi bi-archive-fill',
            command: (item: any) => this.openModalArchive(item.id),
          }
        );
      }
    } else if (step === StatusEnum.ARCHIVED) {
      this.menu = [
        {
          label: this.translateService.translate('valves.list.menu.show'),
          icon: 'bi bi-eye-fill',
          command: (item: any) =>
            this.router.navigate(['valves', 'update', item.id])
        },
      ];
    }
  }

  private publishValve(valveId: string): void {
    this.valveService.publish(valveId).subscribe({
      next: () => {
        this.toasterService.push(
          'success',
          this.translateService.translate('global.success'),
          this.translateService.translate('valves.success.publish')
        );
        this.confirmationService.close();
        this.getValves(this.currentStep);
      },
      error: (error: Error) => {
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate(
            error.message === 'ERR_ROLE'
              ? 'global.error.role'
              : 'valves.error.publish'
          )
        );
      },
    });
  }

  private archiveValve(valveId: string): void {
    this.valveService.archive(valveId).subscribe({
      next: () => {
        this.toasterService.push(
          'success',
          this.translateService.translate('global.success'),
          this.translateService.translate('valves.success.archived')
        );
        this.confirmationService.close();
        this.getValves(this.currentStep);
      },
      error: (error: Error) => {
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate(
            error.message === 'ERR_ROLE'
              ? 'global.error.role'
              : 'valves.error.archived'
          )
        );
      },
    });
  }

  private sendToCorrection(valveId: string): void {
    this.valveService.unpublish(valveId).subscribe({
      next: () => {
        this.toasterService.push(
          'success',
          this.translateService.translate('global.success'),
          this.translateService.translate('valves.success.correction')
        );
        this.confirmationService.close();
        this.getValves(this.currentStep);
      },
      error: (error: Error) => {
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate(
            error.message === 'ERR_ROLE'
              ? 'global.error.role'
              : 'valves.error.correction'
          )
        );
      },
    });
  }

  private getValves(statusId: string): void {
    this.valveService.getAll({ statusId }).subscribe({
      next: (response: Valve[]) => {
        this.loading = false;
        this.tableLoading = false;
        this.items = response;
      },
      error: () => {
        this.loading = false;
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate('valves.error.get-all')
        );
      },
    });
  }
}
